import { Route, Routes, Link, Navigate, useLocation } from 'react-router-dom';
import { AppBar, Box, Button, Container, IconButton, Toolbar, Tooltip, useTheme } from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import Home from './pages/Home.jsx';
import Admin from './pages/Admin.jsx';
import Infos from './pages/Infos.jsx';
import CancelPreregistration from './pages/CancelPreregistration.jsx';
import ConfirmAttendance from './pages/ConfirmAttendance.jsx';
import Pay from './pages/Pay.jsx';
import Recover from './pages/Recover.jsx';
import Saisie from './pages/Saisie.jsx';
import Benevoles from './pages/Benevoles.jsx';
import ConnexionBenevole from './pages/ConnexionBenevole.jsx';
import PlanningBenevoles from './pages/PlanningBenevoles.jsx';
import { useThemeMode } from './themeMode.jsx';
import { useActiveRoles } from './useActiveRoles.js';

const CLUB_TITLE = "Sport Quilles Séverac d'Aveyron";

export default function App() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const { mode, toggle } = useThemeMode();
  const theme = useTheme();
  const roles = useActiveRoles();
  const showSaisie = roles.admin || roles.saisie;
  const onSaisie = location.pathname.startsWith('/saisie');
  const onInfos = location.pathname.startsWith('/infos');
  const onBenevoles = location.pathname.startsWith('/benevoles')
    || location.pathname.startsWith('/connexion/benevole');

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        className="no-print"
        sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor: theme.palette.tints?.headerBg ?? 'rgba(255,255,255,0.85)',
          '@media print': { display: 'none !important' },
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ gap: 2 }}>
            <Box
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minWidth: 0,
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src="/logo-severac.png"
                alt={CLUB_TITLE}
                sx={{ height: { xs: 40, sm: 48 }, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
              />
            </Box>

            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, alignItems: 'center' }}>
              <Button
                component={Link}
                to="/"
                color={location.pathname === '/' ? 'primary' : 'inherit'}
              >
                Préréservation
              </Button>
              <Button
                component={Link}
                to="/infos"
                color={onInfos ? 'primary' : 'inherit'}
                startIcon={<InfoOutlinedIcon fontSize="small" />}
              >
                Infos & plan
              </Button>
              <Button
                component={Link}
                to={roles.volunteer ? '/benevoles/planning' : '/benevoles'}
                color={onBenevoles ? 'primary' : 'inherit'}
                startIcon={<GroupOutlinedIcon fontSize="small" />}
              >
                Bénévoles
              </Button>
              <Button component={Link} to="/admin" color={isAdmin ? 'primary' : 'inherit'}>
                Admin
              </Button>
              {showSaisie && (
                <Button
                  component={Link}
                  to="/saisie"
                  color={onSaisie ? 'primary' : 'inherit'}
                  startIcon={<PaidOutlinedIcon fontSize="small" />}
                >
                  Caisse
                </Button>
              )}
              <ThemeToggle mode={mode} onToggle={toggle} />
            </Box>

            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.25, alignItems: 'center' }}>
              <Tooltip title="Préréservation">
                <IconButton
                  component={Link}
                  to="/"
                  color={location.pathname === '/' ? 'primary' : 'default'}
                  size="large"
                  aria-label="Préréservation"
                >
                  <HomeOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Infos & plan d'accès">
                <IconButton
                  component={Link}
                  to="/infos"
                  color={onInfos ? 'primary' : 'default'}
                  size="large"
                  aria-label="Infos & plan d'accès"
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bénévoles">
                <IconButton
                  component={Link}
                  to={roles.volunteer ? '/benevoles/planning' : '/benevoles'}
                  color={onBenevoles ? 'primary' : 'default'}
                  size="large"
                  aria-label="Bénévoles"
                >
                  <GroupOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Administration">
                <IconButton
                  component={Link}
                  to="/admin"
                  color={isAdmin ? 'primary' : 'default'}
                  size="large"
                  aria-label="Administration"
                >
                  <AdminPanelSettingsOutlinedIcon />
                </IconButton>
              </Tooltip>
              {showSaisie && (
                <Tooltip title="Caisse / Accueil">
                  <IconButton
                    component={Link}
                    to="/saisie"
                    color={onSaisie ? 'primary' : 'default'}
                    size="large"
                    aria-label="Caisse / Accueil"
                  >
                    <PaidOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}
              <ThemeToggle mode={mode} onToggle={toggle} />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flex: 1, pt: 2 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/infos" element={<Infos />} />
          <Route path="/payer/:id" element={<Pay />} />
          <Route path="/confirmer/:id" element={<ConfirmAttendance />} />
          <Route path="/annuler/:id" element={<CancelPreregistration />} />
          <Route path="/retrouver" element={<Recover />} />
          <Route path="/saisie" element={<Saisie />} />
          <Route path="/benevoles" element={<Benevoles />} />
          <Route path="/benevoles/planning" element={<PlanningBenevoles />} />
          <Route path="/connexion/benevole/:token" element={<ConnexionBenevole />} />
          {/* Anciennes routes QR / scan / impression → redirection douce */}
          <Route path="/tickets/*" element={<Navigate to="/" replace />} />
          <Route path="/scan" element={<Navigate to="/saisie" replace />} />
          <Route path="/cuisine-live" element={<Navigate to="/saisie" replace />} />
          <Route path="/impression" element={<Navigate to="/saisie" replace />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </Box>

      <Box
        component="footer"
        className="no-print"
        sx={{
          py: 3,
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: 13,
          '@media print': { display: 'none !important' },
        }}
      >
        © {new Date().getFullYear()}{' '}
        <Box
          component="a"
          href="https://pitsbi.io"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          pitsbi.io
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Bouton compact dark/light. Affiche l'icône de la cible (la lune en clair pour
 * « passer en sombre », le soleil en sombre pour « passer en clair »).
 */
function ThemeToggle({ mode, onToggle }) {
  const isDark = mode === 'dark';
  return (
    <Tooltip title={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}>
      <IconButton
        onClick={onToggle}
        size="large"
        aria-label={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
      >
        {isDark ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
      </IconButton>
    </Tooltip>
  );
}
